import { create } from 'zustand';
import type { Session } from '@supabase/supabase-js';
import type { MailUser } from '../types';
import { supabase, signInWithGoogle, signOut as supabaseSignOut, fetchMyMailRole, isSupabaseAvailable } from '../lib/supabase';
import { rueckwegVergessen } from '../lib/anmeldeRueckweg';

interface AuthState {
  user: MailUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  initialize: () => Promise<void>;
  loginWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshRole: () => Promise<void>;
}

function extractName(meta: Record<string, unknown>): string {
  const given = (meta.given_name as string) || '';
  const family = (meta.family_name as string) || '';
  if (given || family) return `${given} ${family}`.trim();
  return (meta.full_name as string) || (meta.name as string) || '';
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,

  initialize: async () => {
    if (!supabase) {
      set({ isLoading: false });
      return;
    }

    // Session uebernehmen + Rolle laden. Wird sowohl initial als auch (entkoppelt)
    // aus onAuthStateChange aufgerufen.
    const applySession = async (session: Session | null) => {
      if (!session) {
        set({ isLoading: false, isAuthenticated: false, user: null, token: null });
        return;
      }
      const meta = (session.user.user_metadata || {}) as Record<string, unknown>;
      const role = await fetchMyMailRole();
      const user: MailUser = {
        id: session.user.id,
        email: session.user.email ?? '',
        fullName: role?.full_name || extractName(meta) || session.user.email || '',
        avatarUrl: (meta.avatar_url as string) || (meta.picture as string) || undefined,
        role: role?.role ?? 'viewer',
        quelle: role?.quelle ?? null,
        studios: role?.studios ?? [],
        canApprove: role?.darf_freigeben ?? false,
        canSendWithoutApproval: role?.darf_ohne_freigabe ?? false,
        ohneRolle: !role,
      };
      set({ user, token: session.access_token, isAuthenticated: true, isLoading: false });
    };

    try {
      const { data: { session } } = await supabase.auth.getSession();
      await applySession(session);
    } catch (err) {
      console.error('Auth-Initialisierung fehlgeschlagen:', err);
      set({ isLoading: false });
    }

    // WICHTIG: Innerhalb des onAuthStateChange-Callbacks duerfen keine weiteren
    // Supabase-Calls awaited werden — der Callback laeuft, waehrend supabase-js
    // den internen Auth-Lock haelt. Ein await auf rpc()/getSession() darin
    // blockiert den Lock dauerhaft und die ganze App haengt auf dem
    // Ladebildschirm. Darum: synchron entscheiden, Nacharbeit per setTimeout(0)
    // aus dem Lock herausziehen.
    supabase.auth.onAuthStateChange((event, newSession) => {
      if (event === 'SIGNED_OUT' || !newSession) {
        set({ user: null, token: null, isAuthenticated: false });
        return;
      }
      if (event === 'TOKEN_REFRESHED') {
        // Nur Token erneuern — Rolle/Profil unveraendert, kein RPC noetig.
        set({ token: newSession.access_token });
        return;
      }
      const currentUser = get().user;
      if (currentUser?.id === newSession.user.id) {
        // Gleicher User (z.B. Tab-Fokus, INITIAL_SESSION) — kein Neu-Laden der Rolle.
        set({ token: newSession.access_token, isAuthenticated: true });
        return;
      }
      setTimeout(() => {
        applySession(newSession).catch((err) => {
          console.error('Auth-Update fehlgeschlagen:', err);
          set({ isLoading: false });
        });
      }, 0);
    });
  },

  loginWithGoogle: async () => {
    if (!isSupabaseAvailable) {
      return { success: false, error: 'Supabase ist nicht konfiguriert. Bitte .env prüfen.' };
    }
    // Normale Anmeldung führt auf „/“ — ein liegengebliebener Rückweg vom Feedback-Link zählt nicht.
    rueckwegVergessen();
    const { error } = await signInWithGoogle();
    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  logout: async () => {
    await supabaseSignOut();
    set({ user: null, token: null, isAuthenticated: false });
  },

  refreshRole: async () => {
    const current = get().user;
    if (!current) return;
    const role = await fetchMyMailRole();
    set({ user: {
      ...current,
      role: role?.role ?? 'viewer',
      fullName: role?.full_name || current.fullName,
      quelle: role?.quelle ?? null,
      studios: role?.studios ?? [],
      canApprove: role?.darf_freigeben ?? false,
      canSendWithoutApproval: role?.darf_ohne_freigabe ?? false,
      ohneRolle: !role,
    } });
  },
}));

if (typeof window !== 'undefined') {
  useAuthStore.getState().initialize();
}
